import { Alert, Button, Form, Input, Space, Typography } from "antd";
import type { ComponentProps, ComponentType } from "react";
import type { File as EntityFile } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import {
    DatePicker as ZhDatePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber,
    Upload as ZhUpload,
    type UploadProps as ZhUploadProps
} from "zhihao-ui-react";

import request from "@/api";

export interface ServiceCostBillVerificationValues {
    date: string;
    amount: number;
    fileId?: number;
}

export type ServiceCostBillVerificationFile = Pick<EntityFile, "id" | "name" | "path">;

export interface ServiceCostBillVerificationModalProps {
    open: boolean;
    billAmount?: number;
    loading?: boolean;
    disabled?: boolean;
    onCancel: () => void;
    onSubmit: (values: ServiceCostBillVerificationValues) => void;
    uploadFile?: (file: Blob) => Promise<ServiceCostBillVerificationFile>;
}

const defaultUploadFile = async (file: Blob): Promise<ServiceCostBillVerificationFile> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await request.service.post<ServiceCostBillVerificationFile>(
        "/dataService/admin/file/upload",
        formData,
        { timeout: 0 }
    );
    return data;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

// beta.13 的类型声明暂未暴露 controls，运行时仍兼容旧 Vue 的无按钮数字输入配置。
type VerificationMoneyInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const VerificationMoneyInput = ZhInputNumber as ComponentType<VerificationMoneyInputProps>;

/** 复用 Vue 核销弹窗字段，上传成功后将服务端文件 ID 回填到旧核销契约。 */
export function ServiceCostBillVerificationModal({
    open,
    billAmount,
    loading = false,
    disabled = false,
    onCancel,
    onSubmit,
    uploadFile = defaultUploadFile
}: ServiceCostBillVerificationModalProps) {
    const [form] = Form.useForm<ServiceCostBillVerificationValues>();
    const [uploadedFile, setUploadedFile] = useState<ServiceCostBillVerificationFile>();
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    useEffect(() => {
        if (!open) return;
        setUploadError("");
        setUploadedFile(undefined);
        form.setFieldsValue({ amount: billAmount, fileId: undefined });
    }, [billAmount, form, open]);

    // 对齐 Vue beforeUpload：核销凭证只允许单个 PDF/ZIP，大小不能超过 10MB。
    const beforeUpload: ZhUploadProps["beforeUpload"] = file => {
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (extension !== "pdf" && extension !== "zip") {
            setUploadError("上传失败，仅支持 pdf和zip 文件");
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            setUploadError("上传的文件最大不超过10M");
            return false;
        }
        if (uploadedFile) {
            setUploadError("只允许上传一个核销凭证,请先删除后再上传");
            return false;
        }
        setUploadError("");
        setUploading(true);
        void uploadFile(file)
            .then(fileData => {
                if (!fileData?.id) throw new Error("上传响应缺少文件 ID");
                setUploadedFile(fileData);
                form.setFieldsValue({ fileId: Number(fileData.id) });
            })
            .catch(() => {
                setUploadError("核销凭证上传失败");
            })
            .finally(() => {
                setUploading(false);
            });
        return false;
    };

    // 删除仅清理本地回填值，不删除服务端文件，保持 Vue 弹窗行为。
    const removeUploadedFile = () => {
        setUploadedFile(undefined);
        form.setFieldsValue({ fileId: undefined });
        setUploadError("");
    };

    return (
        <ZhDialog
            title="核销账单"
            open={open}
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button
                        type="primary"
                        loading={loading || uploading}
                        disabled={disabled || uploading}
                        onClick={() => void form.submit()}
                    >
                        确定
                    </Button>
                </Space>
            }
        >
            <Form<ServiceCostBillVerificationValues>
                form={form}
                layout="vertical"
                initialValues={{ amount: billAmount }}
                onFinish={values =>
                    onSubmit({
                        ...values,
                        amount: Number(values.amount),
                        fileId:
                            values.fileId == null || values.fileId === ("" as unknown as number)
                                ? undefined
                                : Number(values.fileId)
                    })
                }
            >
                <ZhGrid cols={2} gap={[16, 0]}>
                    <ZhGridItem span={2}>
                        <Form.Item label="日期" name="date" rules={[{ required: true, message: "请选择核销日期" }]}>
                            <ZhDatePicker
                                aria-label="核销日期"
                                className="w-full"
                                format="YYYY-MM-DD"
                                placeholder="请选择日期"
                                type="date"
                                valueFormat="YYYY-MM-DD"
                            />
                        </Form.Item>
                    </ZhGridItem>
                    <ZhGridItem span={2}>
                        <div className="rounded bg-[#f7f7f7] p-4">
                            <ZhGrid cols={2} gap={[16, 0]}>
                                <ZhGridItem>
                                    <Typography.Text>账单金额：{formatVerificationAmount(billAmount)}</Typography.Text>
                                </ZhGridItem>
                                <Form.Item
                                    label="实际核销金额"
                                    name="amount"
                                    rules={[
                                        { required: true, message: "请输入实际核销金额" },
                                        { type: "number", min: 1, max: 10000000000, message: "实际核销金额必须大于 0" }
                                    ]}
                                >
                                    <VerificationMoneyInput
                                        aria-label="实际核销金额"
                                        min={1}
                                        max={10000000000}
                                        precision={2}
                                        controls={false}
                                        className="w-full"
                                    />
                                </Form.Item>
                                <Form.Item label="核销凭证ID" name="fileId" hidden>
                                    <Input aria-label="核销凭证ID" inputMode="numeric" />
                                </Form.Item>
                            </ZhGrid>
                        </div>
                    </ZhGridItem>
                    <ZhGridItem span={2}>
                        <ZhUpload
                            action="#"
                            accept=".pdf,.zip"
                            beforeUpload={beforeUpload}
                            showFileList={false}
                            disabled={uploading}
                        >
                            <Button type="dashed" loading={uploading}>
                                上传核销凭证
                            </Button>
                        </ZhUpload>
                        <Typography.Text type="secondary">支持上传 zip 文件和 pdf 文件，最大不超过 10M</Typography.Text>
                        {uploadedFile ? (
                            <Space className="mt-2" size="small">
                                <Typography.Text>{uploadedFile.name || `文件 ${uploadedFile.id}`}</Typography.Text>
                                <Button type="link" onClick={removeUploadedFile}>
                                    删除
                                </Button>
                            </Space>
                        ) : null}
                        {uploadError ? (
                            <Alert className="mt-2" message={uploadError} role="alert" type="error" showIcon />
                        ) : null}
                    </ZhGridItem>
                </ZhGrid>
            </Form>
        </ZhDialog>
    );
}

/** 账单金额沿用 Vue formatNumber(amount, 2) 的两位小数展示。 */
const formatVerificationAmount = (value?: number) =>
    value === undefined || value === null
        ? "0.00"
        : Number(value).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });
